import React, { useEffect, useState } from 'react';
import client from 'utils/client';
import { Formik, Form } from 'formik';
import Select from 'react-select';
import { useToast } from 'components/Toast/useToast';
import TextInput from 'components/FormElements/TextInput';
import { useAuth } from 'contexts/AuthContext';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import Switch from '@mui/material/Switch';
import './settings.css';

function updateSettings(payload) {
  return client.post('/settings', payload);
}

const SYNC_INTERVAL_LABELS = {
  '5m': 'Every 5 minutes',
  '15m': 'Every 15 minutes',
  '30m': 'Every 30 minutes',
  '1h': 'Every hour',
  '2h': 'Every 2 hours',
  '6h': 'Every 6 hours',
  '12h': 'Every 12 hours',
  '24h': 'Every 24 hours',
};

export default function Settings() {
  const { isAdmin } = useAuth();
  const [tabValue, setTabValue] = useState('feeSettings');
  const [loading, setLoading] = useState(false);
  const [settingsData, setSettingsData] = useState(null);
  const [providers, setProviders] = useState([]);
  const [selectedProviderId, setSelectedProviderId] = useState(null);
  const [error, setError] = useState(null);
  const [transferSettlementEnabled, setTransferSettlementEnabled] = useState(false);
  const [syncInterval, setSyncInterval] = useState('5m');
  const [settlementSaving, setSettlementSaving] = useState(false);
  const { onOpen } = useToast();

  const defaultInitialValues = {
    transactionFee: '',
    transactionFeeCap: '',
    notes: '',
  };

  React.useEffect(() => {
    if (!isAdmin) window.location = '/';
  }, [isAdmin]);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      client.get('/settings'),
      client.get('/terminal-providers'),
    ])
      .then(([settingsRes, providersRes]) => {
        setSettingsData(settingsRes.data.data);
        setProviders(providersRes.data.data || []);
        const settlementSetting = settingsRes.data.data?.find((s) => s.type === 'SETTLEMENT_CONFIG');
        if (settlementSetting) {
          setTransferSettlementEnabled(!!settlementSetting.config?.transfer_settlement_enabled);
          setSyncInterval(settlementSetting.config?.sync_interval || '5m');
        }
      })
      .catch((err) => {
        setError(err);
        onOpen({ type: 'error', message: err?.message || 'Failed to load settings' });
      })
      .finally(() => setLoading(false));
  }, []);

  function handleSubmit(values) {
    if (!selectedProviderId) {
      onOpen({ type: 'error', message: 'Please select a terminal provider' });
      return Promise.resolve();
    }

    let payload;

    if (tabValue === 'feeSettings') {
      payload = {
        feeConfig: {
          name: values.name || 'Fee Configuration',
          transactionFee: values.transactionFee,
          transactionFeeCap: values.transactionFeeCap,
          terminalProviderId: selectedProviderId,
          notes: values.notes || '',
        }
      };
    }

    setLoading(true);
    return updateSettings(payload)
      .then(() => {
        onOpen({ message: 'Settings Updated Successfully' });
        return client.get('/settings');
      })
      .then((response) => {
        setSettingsData(response.data.data);
      })
      .catch((err) => {
        onOpen({ type: 'error', message: err?.message || 'An Error Occurred' });
      })
      .finally(() => setLoading(false));
  }

  const handleChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const getInitialValues = () => {
    if (!settingsData || !settingsData.length || !selectedProviderId) return defaultInitialValues;

    const feeConfig = settingsData.find(
      (item) => item.type === 'FEE_CONFIG' && Number(item.terminal_provider_id) === Number(selectedProviderId),
    );

    if (feeConfig) {
      return {
        ...defaultInitialValues,
        name: feeConfig.name || 'Fee Configuration',
        transactionFee: feeConfig.config?.transaction_fee || '',
        transactionFeeCap: feeConfig.config?.transaction_fee_cap || '',
        notes: feeConfig.notes || '',
      };
    }

    return defaultInitialValues;
  };

  const providerOptions = providers.map((p) => ({ label: p.provider_name, value: p.id }));
  const selectedProviderOption = providerOptions.find((o) => o.value === selectedProviderId) || null;

  const handleSettlementToggle = async () => {
    const newValue = !transferSettlementEnabled;
    setSettlementSaving(true);
    try {
      await updateSettings({ settlementConfig: { transferSettlementEnabled: newValue } });
      setTransferSettlementEnabled(newValue);
      onOpen({ message: `Transfer settlement ${newValue ? 'enabled' : 'disabled'}` });
    } catch (err) {
      onOpen({ type: 'error', message: err?.message || 'Failed to update settlement setting' });
    } finally {
      setSettlementSaving(false);
    }
  };

  const handleSyncIntervalChange = async (e) => {
    const newInterval = e.target.value;
    setSettlementSaving(true);
    try {
      await updateSettings({ settlementConfig: { syncInterval: newInterval } });
      setSyncInterval(newInterval);
      onOpen({ message: `Sync interval updated to ${SYNC_INTERVAL_LABELS[newInterval]}` });
    } catch (err) {
      onOpen({ type: 'error', message: err?.message || 'Failed to update sync interval' });
    } finally {
      setSettlementSaving(false);
    }
  };

  const renderTabContent = () => {
    if (loading && !settingsData) {
      return <div className="settings-loading">Loading settings...</div>;
    }

    const formInitialValues = getInitialValues();

    switch (tabValue) {
      case 'feeSettings':
        return (
          <>
            <div className='add-settings-form' style={{ marginBottom: 16 }}>
              <div className='form-wrapper'>
                <div className='personal-details'>
                  <div className='add-settings-item'>
                    <label>Terminal Provider</label>
                    <Select
                      options={providerOptions}
                      value={selectedProviderOption}
                      onChange={(option) => setSelectedProviderId(option ? option.value : null)}
                      placeholder='Select a terminal provider'
                      isClearable
                    />
                  </div>
                </div>
              </div>
            </div>

            {selectedProviderId ? (
              <Formik initialValues={formInitialValues} onSubmit={handleSubmit} enableReinitialize>
                {({ isSubmitting }) => (
                  <Form className='settings-form'>
                    <div className='form-wrapper'>
                      <section className='form-section'>
                        <header className='form-section-header'>
                          <h4 className='form-section-title'>Bank Transaction Fees</h4>
                          <p className='form-section-description'>Platform-wide defaults applied to every new terminal.</p>
                        </header>
                        <div className='form-grid'>
                          <TextInput
                            className='add-settings-item form-field-md'
                            name='transactionFee'
                            label='Bank Transaction Fee (%)'
                            placeholder='0.5'
                            type='number'
                            min='0'
                            step='0.01'
                            required
                          />
                          <TextInput
                            className='add-settings-item form-field-md'
                            name='transactionFeeCap'
                            label='Bank Transaction Fee Cap'
                            placeholder='1000'
                            type='number'
                            min='0'
                            step='0.01'
                            required
                          />
                          <TextInput
                            className='add-settings-item form-field-full'
                            name='notes'
                            label='Notes'
                            placeholder='Additional information about fee configuration'
                          />
                        </div>
                      </section>
                    </div>
                    <div className='settings-actions'>
                      <button
                        type='submit'
                        className='btn-primary'
                        disabled={isSubmitting || loading}
                      >
                        {isSubmitting ? 'Saving…' : 'Save Changes'}
                      </button>
                    </div>
                  </Form>
                )}
              </Formik>
            ) : (
              <div className='settings-loading'>Select a provider to view or edit its fee configuration.</div>
            )}
          </>
        );
      case 'settlement':
        return (
          <div className='form-wrapper'>
            <section className='form-section'>
              <header className='form-section-header'>
                <h4 className='form-section-title'>Settlement</h4>
                <p className='form-section-description'>Control how settlements are processed across the platform.</p>
              </header>
              <div className='settings-toggle-row'>
                <div className='settings-toggle-info'>
                  <span className='settings-toggle-label'>Transfer Settlement</span>
                  <span className='settings-toggle-description'>
                    When enabled, settlements for transfer transactions will be processed. Disable to pause all transfer settlements.
                  </span>
                </div>
                <Switch
                  checked={transferSettlementEnabled}
                  onChange={handleSettlementToggle}
                  disabled={settlementSaving}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': { color: '#4caf50' },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#4caf50' },
                  }}
                />
              </div>
              <div className='settings-toggle-row' style={{ marginTop: 12 }}>
                <div className='settings-toggle-info'>
                  <span className='settings-toggle-label'>Sync Interval</span>
                  <span className='settings-toggle-description'>
                    How often the system fetches and processes new transfer transactions.
                  </span>
                </div>
                <select
                  className='settings-select'
                  value={syncInterval}
                  onChange={handleSyncIntervalChange}
                  disabled={settlementSaving}
                >
                  {Object.entries(SYNC_INTERVAL_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
            </section>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className='settings'>
      <h1 className='settings-title'>System Settings</h1>
      <Box sx={{ width: '100%' }}>
        <Tabs
          value={tabValue}
          onChange={handleChange}
          aria-label='settings tabs'
          sx={{
            '& .MuiTab-root': {
              textTransform: 'uppercase',
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: '0.06em',
              color: 'var(--color-text-muted)',
            },
            '& .MuiTab-root.Mui-selected': { color: 'var(--color-brand)' },
            '& .MuiTabs-indicator': { backgroundColor: 'var(--color-brand)' },
          }}
        >
          <Tab value="feeSettings" label="Fee Settings" />
          <Tab value="settlement" label="Settlement" />
        </Tabs>
        <Box sx={{ padding: '20px 0' }}>
          {renderTabContent()}
        </Box>
      </Box>
      {error && !loading && (
        <div className="settings-error">
          Failed to load settings. Please try again later.
        </div>
      )}
    </div>
  );
}